import React from "react";
import ReactDOM from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import "./index.css";
import "./i18n";
import i18n from "./i18n";
import Main from "./routes/Main";
import reportWebVitals from "./reportWebVitals";
import { wallGeometryCache } from "./utils/WallGeometryCache";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  ApolloLink,
  fromPromise,
  split,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { notifyGraphQLWsConnected } from "./utils/graphqlWsReconnect";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import ErrorPage from "./routes/Error";
import PropertyPanel from "./components/Views/PropertyPanel";
import Login from "./routes/Login";
import { configureThreeConsoleWarnings } from "./utils/threeConsoleWarnings";
import { initPosthog } from "./utils/posthog";

configureThreeConsoleWarnings();

// Clear geometry cache on app initialization to prevent stale cache from page refresh
// This fixes the issue where wall fill areas expand outside outlines after browser refresh
wallGeometryCache.clearAllCache();

// Initialize PostHog for product/behavioral analytics
initPosthog();

const httpLink = createUploadLink({
  uri: process.env.REACT_APP_SERVER_URI,
  credentials: "include",
  // Required by Apollo Server CSRF prevention for multipart file uploads
  headers: {
    "Apollo-Require-Preflight": "true",
  },
});

let isRefreshing = false;
let pendingRequests: (() => void)[] = [];

const resolvePendingRequests = () => {
  pendingRequests.map((callback) => callback());
  pendingRequests = [];
};

const refreshTokens = async () => {
  try {
    const response = await fetch(process.env.REACT_APP_SERVER_URI ?? "", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        query: `
          mutation refreshTokens {
            refreshTokens {
              accessToken
              refreshToken
            }
          }
        `,
      }),
    });
    const result = await response.json();
    if (result.data && result.data.refreshTokens) {
      document.cookie = `auth_token=${result.data.refreshTokens.accessToken}; refresh_token=${result.data.refreshTokens.refreshToken}; path=/; secure; HttpOnly;`;
      return result.data.refreshTokens;
    }
  } catch (error) {
    console.error("Failed to refresh access token:", error);
    // redirect to login page since refresh token is invalid (we are not in a React component)
    window.location.href = "/login?redirect=" + window.location.pathname;
  }
};

// const errorLink = onError(
//   ({ graphQLErrors, networkError, operation, forward }) => {
//     if (graphQLErrors) {
//       if (process.env.NODE_ENV === "development") {
//         console.log({ graphQLErrors });
//       }
//       for (const err of graphQLErrors) {
//         switch (err.extensions?.code) {
//           case "UNAUTHENTICATED":
//             if (!isRefreshing) {
//               isRefreshing = true;
//               return fromPromise(
//                 refreshTokens()
//                   .then((newAccessToken) => {
//                     resolvePendingRequests();
//                     return newAccessToken;
//                   })

//                   .catch(() => {
//                     pendingRequests = [];
//                     return;
//                   })

//                   .finally(() => {
//                     isRefreshing = false;
//                   }),
//               )
//                 .filter((value) => Boolean(value))
//                 .flatMap(() => forward(operation));
//             }
//             return fromPromise(
//               new Promise<void>((resolve) => {
//                 pendingRequests.push(() => resolve());
//               }),
//             )
//               .filter((value) => Boolean(value))
//               .flatMap(() => forward(operation));
//         }
//       }
//     }
//     if (networkError) {
//       console.error("Network error:", networkError);
//     }
//   },
// );

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      if (process.env.NODE_ENV === "development") {
        console.log({ graphQLErrors });
      }
      for (const err of graphQLErrors) {
        // Check specifically for "invalid_token"
        if (err.message === "invalid_token") {
          window.location.href = "/login?redirect=" + window.location.pathname;
          return; // Exit immediately to avoid retry logic
        }

        switch (err.extensions?.code) {
          case "UNAUTHENTICATED":
            // Proceed with token refresh only if it's a general "UNAUTHENTICATED" error
            if (!isRefreshing) {
              isRefreshing = true;
              return fromPromise(
                refreshTokens()
                  .then((newTokens) => {
                    resolvePendingRequests();
                    return newTokens;
                  })
                  .catch(() => {
                    pendingRequests = [];
                    window.location.href = "/login?redirect=" + window.location.pathname;
                    return;
                  })
                  .finally(() => {
                    isRefreshing = false;
                  }),
              )
                .filter((value) => Boolean(value))
                .flatMap(() => forward(operation));
            }
            // Queue the request if a refresh is in progress
            return fromPromise(
              new Promise<void>((resolve) => {
                pendingRequests.push(() => resolve());
              }),
            )
              .filter((value) => Boolean(value))
              .flatMap(() => forward(operation));
        }
      }
    }
    if (networkError) {
      console.error("Network error:", networkError);
    }
  },
);

const authLink = setContext((_, { headers }) => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("auth_token="));
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token.split("=")[1]}` : "",
    },
  };
});

/** Subscriptions over graphql-ws; queries/mutations stay on the upload HTTP link. */
const wsLink = new GraphQLWsLink(
  createClient({
    url: process.env.REACT_APP_SERVER_URI?.replace("http", "ws") as string,
    connectionParams: () => {
      const token = document.cookie
        .split("; ")
        .find((row) => row.startsWith("auth_token="))
        ?.split("=")[1];
      return { authToken: token };
    },
    on: {
      connected: () => notifyGraphQLWsConnected(),
      error: (err) => console.error("WebSocket error:", err),
    },
  }),
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink as any,
);

export const client = new ApolloClient({
  // errorLink/authLink wrap the split, matching boodil-client, so subscription
  // UNAUTHENTICATED errors can refresh the token and retry.
  link: ApolloLink.from([errorLink, authLink, splitLink]),
  cache: new InMemoryCache(),
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

root.render(
  <React.StrictMode>
    <I18nextProvider i18n={i18n}>
      <ApolloProvider client={client}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            <Route path="/floorplan/" element={<Main client={client} />} />
            <Route path="/floorplan/:id" element={<Main client={client} />} />
            <Route path="login" element={<Login />} />
            <Route path="*" element={<ErrorPage />} />
          </Routes>
        </BrowserRouter>
      </ApolloProvider>
    </I18nextProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
